// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Drafts for in-progress filing and maintenance (design doc §10.2, US-A-05).
 *
 * **Two copies, the newer one wins.** Each draft is kept in this browser's IndexedDB and
 * on the server (`/api/v1/drafts`). The server copy is **shared**: one per form and subject,
 * whoever started it, so the next operator — on this workstation or another — can finish a
 * machine someone else half-filed, and the list says who saved it last. The local copy is
 * what makes a dropped connection cost nothing — a save the server did not get is still
 * kept here, and the next save that gets through brings the server up to date. Reading
 * takes whichever was changed later; deleting removes both.
 *
 * IndexedDB rather than one `localStorage` string: a filing draft carries a slot map that
 * can run to hundreds of entries, and `localStorage` is a synchronous ~5 MB bucket shared
 * with everything else on the origin.
 *
 * Drafts are deleted the moment a submission succeeds — but kept on a 400, a 409 or a
 * network failure, which are exactly the cases where the work is still needed.
 *
 * The local key includes the user id: this browser's copies are the signed-in user's own
 * saves, and what someone else saved reaches the next operator through the server copy,
 * with their name on it.
 *
 * `schemaVersion` is stored alongside each draft. A draft written by an older build may not
 * match what the current form expects, and restoring it silently would put stale values
 * into fields that have since changed meaning — so a mismatch is discarded rather than
 * guessed at.
 */

import { type DBSchema, type IDBPDatabase, openDB } from "idb";
import { api } from "@/api/endpoints";
import type { ServerDraft } from "@/api/types";

const DB_NAME = "cybertron";
const DB_VERSION = 1;
const STORE = "drafts";

/** Bump when a draft's shape changes in a way an older one cannot satisfy. */
export const DRAFT_SCHEMA_VERSION = 1;

export type WorkflowType = "archive-create" | "module-create" | "maintenance";

export interface Draft<T = unknown> {
  key: string;
  userId: string;
  workflow: WorkflowType;
  subject: string;
  schemaVersion: number;
  updatedAt: number;
  payload: T;
  /** Who saved it last, from the server copy; null for one saved in this browser, which
   *  is the signed-in user's own. */
  updatedBy?: string | null;
}

interface CybertronDb extends DBSchema {
  drafts: { key: string; value: Draft; indexes: { "by-user": string } };
}

let database: Promise<IDBPDatabase<CybertronDb>> | null = null;

function db(): Promise<IDBPDatabase<CybertronDb>> {
  database ??= openDB<CybertronDb>(DB_NAME, DB_VERSION, {
    upgrade(instance) {
      const store = instance.createObjectStore(STORE, { keyPath: "key" });
      store.createIndex("by-user", "userId");
    },
  });
  return database;
}

export function draftKey(userId: string, workflow: WorkflowType, subject: string): string {
  return `${userId}::${workflow}::${subject}`;
}

export async function saveDraft<T>(
  userId: string,
  workflow: WorkflowType,
  subject: string,
  payload: T,
): Promise<boolean> {
  const updatedAt = Date.now();
  const [local, server] = await Promise.all([
    saveLocal({
      key: draftKey(userId, workflow, subject),
      userId,
      workflow,
      subject,
      schemaVersion: DRAFT_SCHEMA_VERSION,
      updatedAt,
      payload,
    }),
    remote(() =>
      api.drafts.save(workflow, subject, {
        schema_version: DRAFT_SCHEMA_VERSION,
        payload,
        updated_at: new Date(updatedAt).toISOString(),
      }),
    ).then((result) => result !== null),
  ]);
  // Saved if either copy was: the result says whether it was, so the page never claims a
  // save that did not happen.
  return local || server;
}

async function saveLocal(draft: Draft): Promise<boolean> {
  try {
    await (await db()).put(STORE, draft);
    return true;
  } catch {
    // Storage can be unavailable (private mode, quota, blocked site data). A draft is a
    // convenience; failing the form over it would be worse than losing it.
    return false;
  }
}

/** A call onto the drafts API: its answer, or null when it failed — offline, signed out,
 *  a 404, a 5xx. The local copy carries on regardless; this never fails the form. */
async function remote<T>(call: () => Promise<T>): Promise<{ value: T } | null> {
  try {
    return { value: await call() };
  } catch {
    return null;
  }
}

function fromServer(userId: string, found: ServerDraft): Draft {
  return {
    key: draftKey(userId, found.workflow as WorkflowType, found.subject),
    userId,
    workflow: found.workflow as WorkflowType,
    subject: found.subject,
    schemaVersion: found.schema_version,
    updatedAt: Date.parse(found.updated_at),
    payload: found.payload,
    updatedBy: found.updated_by_email ?? null,
  };
}

/** The later of two copies of one draft. */
function newer(a: Draft | null | undefined, b: Draft | null | undefined): Draft | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return b.updatedAt > a.updatedAt ? b : a;
}

export async function loadDraft<T>(
  userId: string,
  workflow: WorkflowType,
  subject: string,
): Promise<Draft<T> | null> {
  try {
    const [local, server] = await Promise.all([
      (async () => (await db()).get(STORE, draftKey(userId, workflow, subject)))().catch(
        () => undefined,
      ),
      remote(() => api.drafts.get(workflow, subject)),
    ]);
    const found = newer(local, server && fromServer(userId, server.value));
    if (!found) return null;
    if (found.schemaVersion !== DRAFT_SCHEMA_VERSION) {
      // Written by an older build. Restoring it could put stale values into fields whose
      // meaning has changed, so it is dropped rather than guessed at (§10.2).
      await deleteDraft(userId, workflow, subject);
      return null;
    }
    return found as Draft<T>;
  } catch {
    return null;
  }
}

export async function deleteDraft(
  userId: string,
  workflow: WorkflowType,
  subject: string,
): Promise<void> {
  await Promise.all([
    (async () => (await db()).delete(STORE, draftKey(userId, workflow, subject)))().catch(
      // Same reasoning as `saveLocal`.
      () => undefined,
    ),
    remote(() => api.drafts.remove(workflow, subject)),
  ]);
}

/** Everything in progress — for a "resume" list: everyone's on the server, and this
 * user's not yet saved there. */
export async function listDrafts(userId: string): Promise<Draft[]> {
  const [local, server] = await Promise.all([
    (async () => (await db()).getAllFromIndex(STORE, "by-user", userId))().catch(
      () => [] as Draft[],
    ),
    remote(() => api.drafts.list()),
  ]);
  // One entry per draft, whichever copy is newer: a draft saved on another workstation is
  // listed here too, and one saved here while offline is listed before the server has it.
  const byKey = new Map<string, Draft>();
  const serverItems = server?.value.items ?? [];
  for (const draft of [...local, ...serverItems.map((item) => fromServer(userId, item))]) {
    const kept = newer(byKey.get(draft.key), draft);
    if (kept) byKey.set(draft.key, kept);
  }
  return [...byKey.values()]
    .filter((draft) => draft.schemaVersion === DRAFT_SCHEMA_VERSION)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Test seam — closes and forgets the connection so a fresh fake IndexedDB can be used.
 *
 * Closing matters: an open connection blocks `indexedDB.deleteDatabase`, and the delete
 * then never settles. */
export function resetDraftStoreForTests(): void {
  void database?.then((instance) => instance.close()).catch(() => undefined);
  database = null;
}
